/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import {
    AfterContentInit,
    AfterViewInit,
    Component,
    ContentChild,
    ContentChildren,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    QueryList,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { AppCustomPaginatorComponent } from './appcustompaginator/app-custom-paginator.component';
import { BaseComponent } from '../base.component';
import { DownloadService, TableService, UtilsService } from '../../services';
import {
    MatColumnDef,
    MatHeaderRowDef,
    MatNoDataRow,
    MatRowDef,
    MatTable,
    MatTableDataSource
} from '@angular/material/table';
import { TableColumnComponent } from './tableColumn/tableColumn.component';
import { TableMultiSelectModel } from '../../models/tableMultiSelect.model';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-table[id]',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss']
})

export class TableComponent<T> extends BaseComponent implements AfterContentInit, AfterViewInit, OnDestroy {
    /**
     * List of column components for the table.
     */
    @ContentChildren(TableColumnComponent) columns: QueryList<TableColumnComponent>;

    /**
     * List of header row definitions.
     */
    @ContentChildren(MatHeaderRowDef) headerRowDefs: QueryList<MatHeaderRowDef>;

    /**
     * List of column definitions.
     */
    @ContentChildren(MatColumnDef) columnDefs: QueryList<MatColumnDef>;

    /**
     * List of row definitions.
     */
    @ContentChildren(MatRowDef) rowDefs: QueryList<MatRowDef<any>>;

    /**
     * No data row definition.
     */
    @ContentChild(MatNoDataRow) noDataRow: MatNoDataRow;

    /**
     * No data row definition.
     */
    @ContentChild(MatColumnDef) columnDef: MatColumnDef;

    /**
     * MatTable view child.
     */
    @ViewChild(MatTable, { static: true }) public table: MatTable<T>;

    /**
     * Pagination functionality.
     */
    @ViewChild(AppCustomPaginatorComponent) paginator: AppCustomPaginatorComponent;

    /**
     * Optional column definition for 'multi-select' functionality.
     */
    @ViewChild(TableColumnComponent) selectColumn: TableColumnComponent;

    /**
     * Store reference to table container
     */
    @ViewChild('tableContainer') tableContainer: ElementRef;

    /**
     * Show search.
     */
    @Input() public showSearch: boolean = true;

    /**
     * List of search keys to ignore when filtering data.
     */
    @Input() public ignoreSearchKeys: string[] = [];

    /**
     * Show download CSV.
     */
    @Input() public showCSVDownload: boolean = true;

    /**
     * Data setter to be converted to DataSource type.
     * @private
     */
    @Input() set data(data: any[]) {
        if (this.dataChanged(data)) {
            this._data = data;
            this.handleDataLoad(data);
        }
    }

    /**
     * Predefined search term.
     */
    @Input() set searchTerm(searchTerm: string) {
        this._searchTerm = searchTerm;
        const event: any = { target: { value: this.searchTerm } };
        this.applyFilter(event);
    }

    /**
     * Show paginator.
     */
    @Input() set showPaginator(showPaginator: boolean) {
        // TODO: To be added back in when paginator ticket is in development.
        this._showPaginator = false; //showPaginator ? showPaginator : false;
    }

    /**
     * Set the id of the table.
     * @param id
     */
    @Input() set id(id: string) {
        this._id = id;
        this.addDataTableToLookup(id);
    }

    /**
     * Set selected rows.
     * @param rows
     */
    @Input() set selectedRows(rows: any[]) {
        this._selectedRows = Array.isArray(rows) ? rows : [rows];
        this.dataSource['selectedRows'] = this.selectedRows;
        this.tempSelectedRows = this.selectedRows;
        this.updateDataSource();
        this.allPageRowsSelected = this.allRowsSelected();
    }

    /**
     * List of columns to be displayed.
     */
    @Input() public displayedColumns: string[] = [];

    /**
     * Boolean flag for making column fixed.
     */
    @Input() public stickyHeader: boolean = false;

    /**
     * Is component embedded in the wizard.
     */
    @Input() public componentEmbedded: boolean = false;

    /**
     * Store buttons to be positioned next to search bar (mainly for when embedded in tabs).
     */
    @Input() public embeddedButtons: TemplateRef<any>;

    /**
     * Store the text and action of the select all.
     */
    @Input() set multiSelectActions(buttonArray: any[]) {
        this._multiSelectActions = buttonArray;
        this.setSelectAllOnActionsColumn();
    }

    /**
     * Output the selected rows to the parent.
     */
    @Output() public selectedRowsOut: EventEmitter<any[]> = new EventEmitter<any[]>();

    /**
     * Output the matched keys to the parent.
     */
    @Output() public keyMatches: EventEmitter<any> = new EventEmitter();

    /**
     * Output the matched keys to the parent.
     */
    @Output() public searchValues: EventEmitter<any> = new EventEmitter();

    /**
     * Output empty search to the parent.
     */
    @Output() public clearSearchValues: EventEmitter<string> = new EventEmitter();

    /**
     * Data source instance.
     */
    public dataSource: MatTableDataSource<any>;

    /**
     * Store selected rows.
     */
    public _selectedRows: any[] = [];

    /**
     * Store temporary selected rows.
     */
    public tempSelectedRows: any[] = [];

    /**
     * Store the filtered data to send to the paginator.
     */
    public paginatorFilteredData: any[];

    /**
     * Toggles the check all box if all rows on a page are selected.
     */
    public allPageRowsSelected: boolean = false;

    /**
     * Loading state
     */
    public isLoading: boolean = true;

    /**
     * Hold paginator initialisation state.
     */
    public paginatorInitialised: boolean = false;

    /**
     * Store the table data.
     * @private
     */
    private _data: any[] = [];

    /**
     * Store search term for getter.
     * @private
     */
    private _searchTerm: string = null;

    /**
     * Show paginator value for getter/setter.
     * @private
     */
    private _showPaginator: boolean = false;

    /**
     * Store the id of the table.
     * @private
     */
    private _id: string;

    /**
     * Store the multiSelectActions
     * @private
     */
    private _multiSelectActions: TableMultiSelectModel[] = [];

    /**
     * Store the matched keys.
     * @private
     */
    private matchedKeys: string[] = [];

    constructor(
        private downloadService: DownloadService,
        private tableService: TableService,
        private utilsService: UtilsService
    ) {
        super();
    }

    /**
     * Data getter
     */
    get data() {
        return this._data;
    }

    /**
     * Search term getter
     */
    get searchTerm() {
        return this._searchTerm;
    }

    /**
     * Paginator getter.
     */
    get showPaginator() {
        return this._showPaginator;
    }

    /**
     * Table name getter.
     */
    get id() {
        return this._id;
    }

    /**
     * Selected row's getter.
     */
    get selectedRows() {
        return this._selectedRows;
    }

    get multiSelectActions() {
        return this._multiSelectActions;
    }

    public ngAfterContentInit(): void {
        this.setRowAndColumnDefinitions();
        this.subscribeToDataChanges();

        this.table.setNoDataRow(this.noDataRow);
    }

    public ngAfterViewInit(): void {
        if (this.multiSelectActions.length) {
            this.addCheckboxColumn();
            this.setSelectAllOnActionsColumn();
        } else {
            this.setDisplayedColumns();
        }

        this.setDatasourceIdOnColumns();
        this.setFlexGrowOnColumns();
    }

    public ngOnDestroy(): void {
        this.cleanupTables();
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    /**
     * Remove datasource from service after destruction.
     * @private
     */
    private cleanupTables(): void {
        this.tableService.removeTableFromLookup(this.id);
    }

    /**
     * When search is cleared, reset the relevant search.
     */
    public clearSearch(): void {
        this.searchTerm = null;
        this.tempSelectedRows = this.selectedRows;
        this.clearSearchValues.emit('');
        this.dataSource.filter = null;
    }

    /**
     * Filter table data.
     * @param event
     */
    public applyFilter(event: any): void {
        this.utilsService.debounce('searchValue', () => {
            const filterValue: string = typeof event !== 'string' ? (event.target as HTMLInputElement).value : event;
            this.dataSource.filter = filterValue.trim().toLowerCase();
            this.keyMatches.emit(this.matchedKeys);
            this.paginatorFilteredData = this.dataSource.filteredData;
            this.updatedSelectedRows(this.dataSource.filteredData);
        });
    }

    /**
     * Download a copy of the ADC table values.
     */
    public downloadCSV(): void {
        const csv = this.utilsService.convertArrayOfObjectToCSV(this.data);
        const now = new Date();
        const utcTime = new Date(
            Date.UTC(
                now.getUTCFullYear(),
                now.getUTCMonth(),
                now.getUTCDate(),
                now.getUTCHours(),
                now.getUTCMinutes(),
                now.getUTCSeconds()
            )
        );

        const { date, time } = this.utilsService.getDateTimeObjectFromIsoDateTime(utcTime.toISOString());
        const dateTime = date + '-' + time;
        this.downloadService.generateDownload(csv, `${this.id}-${dateTime}.csv`, { type: 'type/plain' });
    }

    /**
     * Handle row selection via keyboard.
     * @param row
     * @param $event
     */
    public onKeyPress(row: any, $event: KeyboardEvent): void {
        if ($event.code === 'Enter') {
            this.selectRow(row, $event);
        }
    }

    /**
     * Determine if all rows have been selected.
     */
    public allRowsSelected(): boolean {
        let start: number;
        let end: number;
        if (this.showPaginator) {
            ({ startIndex: start, endIndex: end } = this.tableService.getIndicesForCurrentPage(this.dataSource));
        } else {
            start = 0;
            end = this.dataSource.filteredData?.length;
        }

        return this.checkRowsExistOnCurrentPage(start, end);
    }

    /**
     * Toggle all rows.
     * @param $event
     */
    public toggleAllRows($event: any): void {
        if ($event) {
            let start: number;
            let end: number;
            if (this.showPaginator) {
                ({ startIndex: start, endIndex: end } = this.tableService.getIndicesForCurrentPage(this.dataSource));
            } else {
                start = 0;
                end = this.dataSource.filteredData.length;
            }
            this.selectedRows = this.getSelectedData(start, end);
        } else {
            this.selectedRows = [];
        }

        this.outputSelectedRows();
        this.allPageRowsSelected = this.allRowsSelected();
    }

    /**
     * Select row.
     * @param row
     * @param $event
     */
    public selectRow(row: any, $event?: any): void {
        if (Array.from($event.target?.classList).includes('usage-help')) {
            return;
        }

        const rows: any[] = this.selectedRows;
        if (!this.isTargetClickExcluded($event)) {
            const indexOfRow: number = this.selectedRows.findIndex(item => this.utilsService.compareData(row, item));
            if (indexOfRow !== -1) {
                rows.splice(indexOfRow, 1);
            } else {
                if (!this.multiSelectActions.length) {
                    rows.splice(0, 1, row);
                } else {
                    rows.push(row);
                }
            }

            this.selectedRows = rows;

            this.outputSelectedRows();
            this.allPageRowsSelected = this.allRowsSelected();
        }
    }

    /**
     * Check if a given row exists in our selected rows collection.
     * @param row
     */
    public selectionHasValue(row: any): boolean {
        if (!this.selectedRows?.length) {
            return false;
        }

        return !!this.selectedRows.find((item: any): boolean => this.utilsService.compareData(row, item));
    }

    /**
     * Check if the target click is excluded.
     * @param $event
     */
    public isTargetClickExcluded($event: any): boolean {
        const target = $event.target;
        if (target.tagName === 'LABEL' || target.tagName === 'BUTTON') {
            return true;
        } else if (target.tagName === 'INPUT' && target.id !== 'selectAll' && target.id !== 'select-individual') {
            return true;
        } else if (target.tagName === 'DIV' || target.tagName === 'IMG') {
            const classes = Array.from(target.classList);
            const classesToExclude = [
                'actions-toggle',
                'split-button-wrapper',
                'button-inner',
                'routerLink',
                'table-row-item',
                'drop-button-text-wrapper'
            ];

            return classes.some((item: any) => classesToExclude.includes(item)) || target.alt === 'Action dots';
        } else if (target.tagName === 'SPAN') {
            const classes = Array.from(target.classList);

            return classes.includes('open');
        }

        return false;
    }

    /**
     * Apply paginator updates.
     */
    public applyPaginatorUpdates(): void {
        this.dataSource.paginator = this.paginator;
        this.allPageRowsSelected = this.allRowsSelected();
    }

    /**
     * Add the datatable to the lookup table in the table service.
     * @param id
     * @private
     */
    private addDataTableToLookup(id: string): void {
        this.tableService.dataSourceLookup[id] = {
            id
        };
    }

    /**
     * Set the datasource for the datatable in the lookup.
     * @private
     */
    private setDataSourceInLookup(): void {
        this.tableService.dataSourceLookup[this.id].dataSource = this.dataSource;
    }

    /**
     * Update selected rows.
     * @param data
     * @private
     */
    private updatedSelectedRows(data: any[]): void {
        // Set the temp selected rows when filtering.
        this.tempSelectedRows = this.selectedRows.filter(row => {
            return data.find(item => this.utilsService.compareData(row, item));
        });

        this.dataSource['selectedRows'] = this.tempSelectedRows;
        this.updateDataSource();
    }

    /**
     * Check data has changed.
     * @param incomingData
     * @private
     */
    private dataChanged(incomingData: any[]): boolean {
        const currentData = this.data;
        const empty: boolean = incomingData.length === 0 && currentData.length === 0;

        if (currentData.length !== incomingData.length || empty) {
            return true;
        } else {
            return !this.utilsService.compareData(incomingData, currentData);
        }
    }

    /**
     * Load the data.
     * @param data
     * @private
     */
    private handleDataLoad(data: any): void {
        this.createInitialDataSource(data);

        this.dataSource.data = data;
        this.updateDataSource();
        this.updatedSelectedRows(data);

        this.isLoading = false;
    }

    /**
     * Create initial datasource.
     * @param data
     * @private
     */
    private createInitialDataSource(data: any): void {
        if (!this.dataSource) {
            this.dataSource = new MatTableDataSource<any>(data);
            this.dataSource['id'] = this.id;
            this.setDataSourceInLookup();
            this.setUpSearchPredicate();
        } else {
            return;
        }
    }

    /**
     * Update the datasource store in the table service.
     * @private
     */
    private updateDataSource(): void {
        this.tableService.dataSourceLookup[this.id].dataSource = this.dataSource;
    }

    /**
     * Override Angular Material's search predicate.
     * @private
     */
    private setUpSearchPredicate(): void {
        this.dataSource.filterPredicate = (data: any, filter: string) => {
            const searchTerms: string[] = this.utilsService.removeSpecialCharactersFromArray(
                this.utilsService.convertStringToArray(filter)
            );

            return this.matchFound(data, searchTerms);
        };
    }

    /**
     * Check the object keys for matches.
     * @param data
     * @param filter
     * @private
     */
    private matchFound(data: any, filter: string[]): boolean {
        let returnVal: boolean;
        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                if (this.ignoreSearchKeys.includes(key)) {
                    continue;
                }

                if (typeof data[key] === 'string' && this.searchHasMatch(data[key], filter)) {
                    this.addMatchedKeys(key);

                    returnVal = true;
                    break;
                }

                if (Array.isArray(data[key]) && this.searchHasMatch(data[key], filter)) {
                    this.addMatchedKeys(key);

                    returnVal = true;
                    break;
                }

                if (typeof data[key] === 'number' && this.searchHasMatch(data[key].toString(), filter)) {
                    this.addMatchedKeys(key);

                    returnVal = true;
                    break;
                }
            }
        }

        if (returnVal) {
            this.searchValues.emit(filter);
        }

        return returnVal;
    }

    /**
     * Check if any of the search terms match the incoming value.
     * @param value
     * @param searchTerms
     * @private
     */
    private searchHasMatch(value: any, searchTerms: string[]): boolean {
        return searchTerms.some(item => this.formatSearchTerm(item).test(value.toString().toLowerCase()));
    }

    /**
     * Format the search term.
     * @param searchTerm
     * @private
     */
    private formatSearchTerm(searchTerm: string): any {
        if (searchTerm === null || searchTerm === undefined) {
            return searchTerm;
        } else {
            return new RegExp(searchTerm, 'gi');
        }
    }

    /**
     * Build array of matched keys.
     * @param key
     * @private
     */
    private addMatchedKeys(key: string): void {
        if (!this.matchedKeys.includes(key)) {
            this.matchedKeys.push(key);
        }
    }

    // TODO: Unused but should stay for when paginator functionality is updated.
    /**
     * Check for pagination initialisation.
     * @private
     */
    private checkForPaginatorInitialisation(): void {
        this.tableService.paginatorInitialised
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (isInitialised: any): void => {
                    if (isInitialised) {
                        this.isLoading = false;
                        this.paginator = isInitialised;
                        this.paginatorInitialised = true;
                        this.dataSource.paginator = this.paginator;
                    }
                }
            });
    }

    /**
     * Subscribe to the sort events.
     * @private
     */
    private subscribeToDataChanges(): void {
        this.columns.forEach((column) => {
            column.sortChange
                .subscribe({
                    next: ($event: any): void => {
                        this.dataSource.sort = $event;
                        this.updateDataSource();
                    }
                });
        });
    }

    /**
     * Set the row and column definitions.
     * @private
     */
    private setRowAndColumnDefinitions(): void {
        this.columns.forEach(ngColumn => this.table.addColumnDef(ngColumn.columnDef));
        this.columnDefs.forEach(columnDef => this.table.addColumnDef(columnDef));
        this.rowDefs.forEach(rowDef => this.table.addRowDef(rowDef));
        this.headerRowDefs.forEach(headerRowDef => this.table.addHeaderRowDef(headerRowDef));
        this.setIndexOnColumns();
    }

    /**
     * Add index property to columns.
     * @private
     */
    private setIndexOnColumns(): void {
        const columns: any[] = this.columns.toArray();
        for (let i = 0; i < columns.length; i++) {
            columns[i]['index'] = i;
        }

        this.columns.reset(columns);
    }

    /**
     * Set the datasource id on the column so it can be referenced.
     * @private
     */
    private setDatasourceIdOnColumns(): void {
        const columns = this.columns.toArray();
        for (const column of columns) {
            column.dataSourceId = this.id;
        }
    }

    /**
     * Add checkbox column to table after initial columns have loaded.
     * @private
     */
    private addCheckboxColumn(): void {
        const newColumns: any[] = this.columns.toArray();
        newColumns.unshift(this.selectColumn);
        this.columns.reset(newColumns);
        this.setRowAndColumnDefinitions();
        this.displayedColumns = this.columns.map(item => item.prop);
    }

    /**
     * Set columns to display to user.
     * @private
     */
    private setDisplayedColumns(): void {
        const newColumns: any[] = this.columns.toArray();
        this.columns.reset(newColumns);
        this.displayedColumns = this.columns.map(item => item.prop);
    }

    /**
     * Assign multiselect functions to the actions columns.
     * @private
     */
    private setSelectAllOnActionsColumn(): void {
        const columns = this.columns.toArray();
        columns[columns.length - 1].selectAll = this.multiSelectActions;
    }

    /**
     * Set the flex grow property on the column.
     * @private
     */
    private setFlexGrowOnColumns(): void {
        this.tableService.columnCount = this.columns.length;
        const newColumns: any[] = this.columns.toArray();
        for (let i = 0; i < newColumns.length; i++) {
            newColumns[i].columnStyle = {
                'flex-grow': newColumns[i].flexGrow
            };
        }
    }

    /**
     * Check the selected rows are all on the same page.
     * @param startIndex
     * @param endIndex
     * @private
     */
    private checkRowsExistOnCurrentPage(startIndex: number, endIndex: number): boolean {
        const filteredData = this.dataSource.filteredData;
        const rows = [];
        for (const row of this.selectedRows) {
            const indexOfRowInRawData = filteredData.findIndex(item => this.utilsService.compareData(row, item));
            if (indexOfRowInRawData >= startIndex && indexOfRowInRawData <= endIndex) {
                rows.push(row);
            }
        }

        return this.showPaginator
            ? !!(rows.length && rows.length === this.dataSource.paginator?.pageSize)
            : !!(rows.length && rows.length === this.dataSource.filteredData?.length);
    }

    /**
     * Get data entries for current page.
     * @param startIndex
     * @param endIndex
     * @private
     */
    private getSelectedData(startIndex: number, endIndex: number): any {
        const selectedData = [];

        const condition: number = endIndex === this.dataSource?.filteredData.length ? this.dataSource?.filteredData.length : (endIndex + 1);

        for (let i = startIndex; i < condition; i++) {
            selectedData.push(this.dataSource.data[i]);
        }

        this.selectedRows = selectedData;

        return selectedData;
    }

    /**
     * Output the selected rows to parent and set in table service.
     * @private
     */
    private outputSelectedRows(): void {
        this.tempSelectedRows = this.selectedRows;
        this.selectedRowsOut.emit(this.selectedRows);
        this.dataSource['selectedRows'] = this.selectedRows;
        this.updateDataSource();
    }
}
